import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  SearchIcon,
  CloseIcon,
  MotorcycleIcon,
  ImageOffIcon,
  ChevronRightIcon,
} from "../lib/icons.js";
import { Input } from "../ui/Input.js";
import { Badge } from "../ui/Badge.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { FilterBar } from "../ui/FilterBar.js";
import { useDebouncedValue } from "../lib/useDebounce.js";
import { searchModels, getCategories } from "../lib/api.js";
import type {
  CategoriesResponse,
  ModelsListResponse,
  ModelSummary,
} from "../lib/types.js";

// Kolik roků zobrazit před “+ N dalších” toggle. Roky přijdou z API seřazené DESC,
// takže se ukáže prvních N nejnovějších; starší ročníky (až do 90. let) se odhalí po kliku.
const YEAR_COLLAPSE_AFTER = 5;

const PAGE_SIZE = 30;

/**
 * Obrazovka B — vyhledávání modelu.
 *
 * Funkce:
 *  - debounced search (300ms) přes popis/artNum
 *  - filtr brand, region (zatím KTM/EU)
 *  - responsive grid 1→2→3 sloupce
 *  - query param `q` persist do URL (share-link friendly)
 *  - pagination via "Načíst další"
 */
export function SearchPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";
  const initialYear = searchParams.get("year");
  const initialCategory = searchParams.get("category");

  const [q, setQ] = useState(initialQ);
  const debouncedQ = useDebouncedValue(q, 300);
  const [year, setYear] = useState<number | null>(
    initialYear ? parseInt(initialYear, 10) : null,
  );
  const [category, setCategory] = useState<string | null>(initialCategory);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ModelsListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<ModelSummary[]>([]);

  const [categoriesData, setCategoriesData] = useState<CategoriesResponse | null>(null);

  // Načti kategorie jednorázově
  useEffect(() => {
    getCategories("ktm", "EU")
      .then(setCategoriesData)
      .catch(() => {});
  }, []);

  // Persist filtry do URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (debouncedQ) next.set("q", debouncedQ);
    else next.delete("q");
    if (year !== null) next.set("year", String(year));
    else next.delete("year");
    if (category) next.set("category", category);
    else next.delete("category");

    const currentStr = searchParams.toString();
    const nextStr = next.toString();
    if (currentStr !== nextStr) {
      setSearchParams(next, { replace: true });
    }
  }, [debouncedQ, year, category, searchParams, setSearchParams]);

  // Při změně jakéhokoli filtru resetuj paginaci
  useEffect(() => {
    setOffset(0);
    setAccumulated([]);
  }, [debouncedQ, year, category]);

  // Fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    searchModels({
      search: debouncedQ || undefined,
      brand: "ktm",
      region: "EU",
      year: year ?? undefined,
      category: category ?? undefined,
      limit: PAGE_SIZE,
      offset,
    })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setAccumulated((prev) => (offset === 0 ? res.items : [...prev, ...res.items]));
      })
      .catch((err) => !cancelled && setError(String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, year, category, offset]);

  const hasMore = useMemo(() => {
    if (!data) return false;
    return accumulated.length < data.total;
  }, [data, accumulated]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">
          Vyhledat model
        </h1>
        <p className="mt-1 text-zinc-500">
          Najděte svou motorku podle názvu, modelového roku nebo article number.
        </p>
      </div>

      <div className="max-w-2xl mb-6">
        <Input
          autoFocus
          sizeVariant="lg"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="např. 125 Duke, 890 Adventure, F9603V1…"
          iconLeft={<SearchIcon size={20} />}
          iconRight={
            q ? (
              <button
                type="button"
                onClick={() => setQ("")}
                className="hover:text-zinc-600"
                aria-label="Smazat"
              >
                <CloseIcon size={18} />
              </button>
            ) : undefined
          }
        />
      </div>

      <div className="space-y-3 mb-8">
        {categoriesData && categoriesData.years.length > 0 && (
          <FilterBar<number>
            label="Rok"
            value={year}
            onChange={setYear}
            collapseAfter={YEAR_COLLAPSE_AFTER}
            options={categoriesData.years.map((y) => ({
              value: y.year,
              label: String(y.year),
            }))}
          />
        )}
        {categoriesData && categoriesData.categories.length > 0 && (
          <FilterBar<string>
            label="Kategorie"
            value={category}
            onChange={setCategory}
            options={categoriesData.categories.map((c) => ({
              value: c.name,
              label: c.name,
              count: c.count,
            }))}
          />
        )}
        {(year !== null || category !== null || debouncedQ) && (
          <div className="flex items-center gap-3">
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-400 shrink-0 w-20">
              &nbsp;
            </div>
            <button
              type="button"
              onClick={() => {
                setQ("");
                setYear(null);
                setCategory(null);
              }}
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs text-zinc-500 hover:text-ktm-orange hover:bg-ktm-orange-50 transition-colors"
            >
              <CloseIcon size={14} />
              Vymazat filtry
            </button>
          </div>
        )}
      </div>

      <ResultsMeta data={data} loading={loading} query={debouncedQ} />

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Chyba načítání: {error}
        </div>
      )}

      {loading && accumulated.length === 0 ? (
        <LoadingBlock label="Načítám modely…" />
      ) : accumulated.length === 0 ? (
        <EmptyState
          icon={<MotorcycleIcon size={28} />}
          title="Žádné modely"
          description={
            debouncedQ
              ? `Pro "${debouncedQ}" jsme nic nenašli. Zkuste jiný text.`
              : "Zadejte dotaz pro vyhledání."
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
            {accumulated.map((m) => (
              <ModelCard key={m.id} model={m} />
            ))}
          </div>

          {hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                disabled={loading}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                className="px-5 h-10 rounded-lg border border-zinc-200 bg-white text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 shadow-card"
              >
                {loading ? "Načítám…" : `Načíst dalších ${PAGE_SIZE}`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ResultsMeta({
  data,
  loading,
  query,
}: {
  data: ModelsListResponse | null;
  loading: boolean;
  query: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between mb-4 text-sm text-zinc-500">
      <div>
        {loading && !data ? (
          <span>Načítám…</span>
        ) : data ? (
          <span>
            <span className="font-semibold text-zinc-900">
              {data.total.toLocaleString("cs-CZ")}
            </span>{" "}
            {data.total === 1 ? "výsledek" : data.total < 5 ? "výsledky" : "výsledků"}
            {query && (
              <>
                {" "}
                pro <span className="text-zinc-900">"{query}"</span>
              </>
            )}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Badge tone="neutral">KTM</Badge>
        <Badge tone="neutral">EU</Badge>
      </div>
    </div>
  );
}

function ModelCard({ model }: { model: ModelSummary }): JSX.Element {
  return (
    <Link
      to={`/model/${model.id}`}
      className="group bg-white rounded-xl border border-zinc-200 shadow-card hover:shadow-card-hover hover:border-ktm-orange-100 transition-all duration-150 overflow-hidden flex flex-col"
    >
      <div className="aspect-[5/3] bg-zinc-50 relative flex items-center justify-center border-b border-zinc-100">
        {model.imageUrl ? (
          <img
            src={model.imageUrl}
            alt={model.description ?? ""}
            className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <ImageOffIcon size={32} className="text-zinc-300" />
        )}
        {model.year && (
          <div className="absolute top-3 left-3">
            <Badge tone="neutral" className="bg-white/90 backdrop-blur">
              {model.year}
            </Badge>
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-zinc-900 leading-snug line-clamp-2 group-hover:text-ktm-orange transition-colors">
              {model.description ?? "—"}
            </h3>
          </div>
          <ChevronRightIcon
            size={16}
            className="text-zinc-300 group-hover:text-ktm-orange group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1"
          />
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500">
          <span className="font-medium text-zinc-700">{model.brand.shortName}</span>
          {model.articleNumber && (
            <>
              <span className="text-zinc-300">·</span>
              <span className="font-mono">{model.articleNumber}</span>
            </>
          )}
          {model.color && (
            <>
              <span className="text-zinc-300">·</span>
              <span>{model.color}</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
